import { useEffect, useState } from 'react';

interface Announcement {
  id: number;
  title: string;
  date: string;
  category: string;
  excerpt: string;
}

export default function LatestAnnouncements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'Product',
    excerpt: '',
  });

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = () => {
    const stored = localStorage.getItem('announcements');
    if (stored) {
      setAnnouncements(JSON.parse(stored));
    } else {
      const defaultAnnouncements = [
        {
          id: 1,
          title: 'New Product Launch - Moringa Tea',
          date: '2024-01-15',
          category: 'Product',
          excerpt: 'Excited to announce our new Moringa Tea product line launching next month...',
        },
        {
          id: 2,
          title: 'Annual Company Meeting - Q1 2024',
          date: '2024-01-12',
          category: 'Event',
          excerpt: 'Join us for the quarterly review and planning session on January 25th...',
        },
        {
          id: 3,
          title: 'Updated Leave Policy',
          date: '2024-01-10',
          category: 'Policy',
          excerpt: 'Please review the updated leave policy effective from February 1st, 2024...',
        },
      ];
      setAnnouncements(defaultAnnouncements);
      localStorage.setItem('announcements', JSON.stringify(defaultAnnouncements));
    }
  };

  const handleAdd = () => {
    if (!formData.title || !formData.excerpt) return;

    const newAnnouncement: Announcement = {
      id: Date.now(),
      title: formData.title,
      date: new Date().toISOString().split('T')[0],
      category: formData.category,
      excerpt: formData.excerpt,
    };

    const updated = [newAnnouncement, ...announcements];
    setAnnouncements(updated);
    localStorage.setItem('announcements', JSON.stringify(updated));
    setIsAddModalOpen(false);
    setFormData({ title: '', category: 'Product', excerpt: '' });
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      category: announcement.category,
      excerpt: announcement.excerpt,
    });
  };

  const handleUpdate = () => {
    if (!editingAnnouncement || !formData.title || !formData.excerpt) return;

    const updated = announcements.map(a =>
      a.id === editingAnnouncement.id
        ? { ...a, title: formData.title, category: formData.category, excerpt: formData.excerpt }
        : a
    );

    setAnnouncements(updated);
    localStorage.setItem('announcements', JSON.stringify(updated));
    setEditingAnnouncement(null);
    setFormData({ title: '', category: 'Product', excerpt: '' });
  };

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    const updated = announcements.filter(a => a.id !== id);
    setAnnouncements(updated);
    localStorage.setItem('announcements', JSON.stringify(updated));
  };

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">Latest Announcements</h2>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-add-line w-4 h-4 flex items-center justify-center"></i>
            Add New
          </button>
        </div>

        <div className="space-y-4">
          {announcements.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No announcements yet</p>
          ) : (
            announcements.map((announcement) => (
              <div key={announcement.id} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-megaphone-line text-teal-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{announcement.title}</h3>
                    <p className="text-xs text-gray-600 mb-2">{announcement.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="px-2 py-1 bg-gray-100 rounded">{announcement.category}</span>
                      <span>{announcement.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Add Announcement</h3>
              <button
                onClick={() => {
                  setIsAddModalOpen(false);
                  setFormData({ title: '', category: 'Product', excerpt: '' });
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="Product">Product</option>
                  <option value="Event">Event</option>
                  <option value="Policy">Policy</option>
                  <option value="News">News</option>
                  <option value="Update">Update</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  rows={4}
                  placeholder="Enter announcement description"
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/500 characters</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAdd}
                  className="flex-1 bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  Add Announcement
                </button>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setFormData({ title: '', category: 'Product', excerpt: '' });
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingAnnouncement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Edit Announcement</h3>
              <button
                onClick={() => {
                  setEditingAnnouncement(null);
                  setFormData({ title: '', category: 'Product', excerpt: '' });
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="Product">Product</option>
                  <option value="Event">Event</option>
                  <option value="Policy">Policy</option>
                  <option value="News">News</option>
                  <option value="Update">Update</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  rows={4}
                  placeholder="Enter announcement description"
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/500 characters</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleUpdate}
                  className="flex-1 bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  Update Announcement
                </button>
                <button
                  onClick={() => {
                    setEditingAnnouncement(null);
                    setFormData({ title: '', category: 'Product', excerpt: '' });
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
